import React, { useEffect, useState } from 'react';
import { Dialog } from '@/shared/components/Dialog';
import { ChevronDown } from 'react-feather';
import { Dropdown } from '@/shared/components/Dropdown';
import { Textarea } from '@/shared/components/TextField';
import { Checkbox } from '@/shared/components/Checkbox';
import {
  UPDATE_SR_STATUS_LABELS as L,
  TECHNICIAN_UPDATE_SR_STATUS_LABELS as TL,
  UPDATE_SR_CANCELLATION_REASONS,
  TECHNICIAN_MISSED_VISIT_REASONS,
  type TechnicianMissedVisitReasonId,
  type UpdateSrCancellationReasonId,
} from '../../constants/labels';
import { SERVICE_MANAGEMENT_LABELS } from '../../constants/translations/labels';
import type { ServiceRequestStatus } from '../../types/serviceRequestListType';
import {
  STATUS_CONFIG,
  formatScheduledDate,
  formatScheduledTime,
} from './viewServiceRequestHelpers';
import { SRStatusBadge } from './viewServiceRequestUI';
import type { UpdateSrStep } from '../../hooks/useUpdateServiceRequestStatus';

export interface UpdateStatusModalSummary {
  scheduledStartAt: string | null | undefined;
  scheduledEndAt: string | null | undefined;
  serviceTypeName: string | null | undefined;
  technicianName: string | null | undefined;
  clientName: string | null | undefined;
}

interface UpdateStatusModalProps {
  open: boolean;
  onClose: () => void;
  currentStatus: ServiceRequestStatus;
  allowedStatuses: ServiceRequestStatus[];

  // Step navigation
  step: UpdateSrStep;
  onContinue: () => void;
  onGoBackToForm: () => void;

  // Status / notes / notify (step 1)
  newStatus: ServiceRequestStatus | '';
  onNewStatusChange: (status: ServiceRequestStatus) => void;
  newStatusError: string;
  adminNotes: string;
  onAdminNotesChange: (value: string) => void;
  adminNotesError: string;
  notify: boolean;
  onNotifyChange: (value: boolean) => void;

  // Cancel-reason step (step 2)
  cancellationReason: UpdateSrCancellationReasonId | '';
  onCancellationReasonChange: (id: UpdateSrCancellationReasonId) => void;
  cancellationReasonError: string;
  customReason: string;
  onCustomReasonChange: (value: string) => void;
  customReasonError: string;

  /**
   * Admin-assignee mode. Hides the "Send email" checkbox — assignee
   * transitions always notify server-side, so the toggle would be a no-op.
   */
  assigneeMode?: boolean;

  // Missed-visit step ( admin-assignee only). Optional so the standard
  // admin-manager usage that never reaches this step can omit them. The
  // comment reuses `adminNotes` / `onAdminNotesChange` / `adminNotesError`.
  missedVisitReason?: TechnicianMissedVisitReasonId | '';
  onMissedVisitReasonChange?: (id: TechnicianMissedVisitReasonId) => void;
  missedVisitReasonError?: string;

  /** Used to render the read-only "Scheduled visit details" card on step 2. */
  summary: UpdateStatusModalSummary;

  isSubmitting: boolean;
  submitError: string;
  onSubmit: () => void;
  canSubmit: boolean;
  canContinue: boolean;
}

const InfoIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="#474A4C" strokeWidth="1.5" />
    <line
      x1="12"
      y1="11"
      x2="12"
      y2="16.5"
      stroke="#474A4C"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="12" cy="8" r="1" fill="#474A4C" />
  </svg>
);

// Matches the value styling of the Reschedule confirmation step
// (RescheduleConfirmationStep) — font 16/500/#474A4C with a muted 12px label.
const ReadOnlyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span
      style={{
        fontFamily: 'Roboto',
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 400,
        letterSpacing: 0.4,
        color: '#6a7278',
      }}
    >
      {label}
    </span>
    <span
      className="break-words"
      style={{
        fontFamily: 'Roboto',
        fontSize: 16,
        lineHeight: '24px',
        fontWeight: 500,
        color: '#474A4C',
      }}
    >
      {value || '—'}
    </span>
  </div>
);

const ScheduledVisitCard: React.FC<{ summary: UpdateStatusModalSummary }> = ({
  summary,
}) => {
  const fmtDate = (iso: string | null | undefined) => {
    const v = formatScheduledDate(iso);
    return v === '—' || v === '-' ? '' : v;
  };
  const fmtTime = (iso: string | null | undefined) => {
    const v = formatScheduledTime(iso);
    return v === '—' || v === '-' ? '' : v;
  };
  return (
    <div
      className="rounded-lg border border-[#DCDDE0]"
      style={{ backgroundColor: '#FFFFFF', padding: 16 }}
      data-testid="update-sr-scheduled-visit-card"
    >
      <h3
        style={{
          fontFamily: 'Roboto',
          fontSize: 18,
          lineHeight: '24px',
          fontWeight: 500,
          letterSpacing: 0.1,
          color: '#474A4C',
          marginBottom: 16,
        }}
      >
        {L.CANCEL_STEP_VISIT_SECTION}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <ReadOnlyField
          label={L.CANCEL_STEP_START_DATE}
          value={fmtDate(summary.scheduledStartAt)}
        />
        <ReadOnlyField
          label={L.CANCEL_STEP_END_DATE}
          value={fmtDate(summary.scheduledEndAt)}
        />
        <ReadOnlyField
          label={L.CANCEL_STEP_START_TIME}
          value={fmtTime(summary.scheduledStartAt)}
        />
        <ReadOnlyField
          label={L.CANCEL_STEP_END_TIME}
          value={fmtTime(summary.scheduledEndAt)}
        />
        <ReadOnlyField
          label={L.CANCEL_STEP_SERVICE_TYPE}
          value={summary.serviceTypeName ?? ''}
        />
        <ReadOnlyField
          label={L.CANCEL_STEP_TECHNICIAN}
          value={summary.technicianName ?? ''}
        />
        <div className="md:col-span-2">
          <ReadOnlyField
            label={L.CANCEL_STEP_CLIENT}
            value={summary.clientName ?? ''}
          />
        </div>
      </div>
    </div>
  );
};

const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({
  open,
  onClose,
  currentStatus,
  allowedStatuses,
  step,
  onContinue,
  onGoBackToForm,
  newStatus,
  onNewStatusChange,
  newStatusError,
  adminNotes,
  onAdminNotesChange,
  adminNotesError,
  notify,
  onNotifyChange,
  cancellationReason,
  onCancellationReasonChange,
  cancellationReasonError,
  customReason,
  onCustomReasonChange,
  customReasonError,
  assigneeMode = false,
  missedVisitReason = '',
  onMissedVisitReasonChange,
  missedVisitReasonError = '',
  summary,
  isSubmitting,
  submitError,
  onSubmit,
  canSubmit,
  canContinue,
}) => {
  const hasTransitions = allowedStatuses.length > 0;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isReasonDropdownOpen, setIsReasonDropdownOpen] = useState(false);
  useEffect(() => {
    if (!open) {
      setIsDropdownOpen(false);
      setIsReasonDropdownOpen(false);
    }
  }, [open]);
  const displayNewStatusError = isDropdownOpen ? '' : newStatusError;
  const displayCancellationReasonError = isReasonDropdownOpen
    ? ''
    : cancellationReasonError;
  const displayMissedVisitReasonError = isReasonDropdownOpen
    ? ''
    : missedVisitReasonError;

  const isCancelling = newStatus === 'cancelled';
  const isUnfulfilled = newStatus === 'unfulfilled';
  // `cancelled` (cancel-reason) and `unfulfilled` (missed-visit, RR-916) both
  // collect extra input on a dedicated second step instead of submitting from
  // the form. Their step-1 primary button reads "Continue", and the optional
  // notes / notify controls are hidden (collected on the detail step).
  const needsDetailStep = isCancelling || isUnfulfilled;
  const isMissedVisitStep = step === 'missed-visit';
  const showScheduledVisit =
    (step === 'cancel-reason' && currentStatus === 'assigned') ||
    isMissedVisitStep;

  const items = allowedStatuses.map((status) => {
    const cfg = STATUS_CONFIG[status];
    const text = cfg?.text ?? '#9CA3AF';
    const bg = cfg?.bg ?? '#F3F4F6';
    const labelText = cfg?.label ?? status;
    return {
      id: status,
      label: '',
      toolTip: false as const,
      leadingIcon: (
        <span
          className="inline-flex items-center gap-2 rounded-lg"
          style={{
            backgroundColor: bg,
            color: text,
            paddingLeft: 8,
            paddingRight: 12,
            paddingTop: 6,
            paddingBottom: 6,
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: '0.1px',
            fontWeight: 500,
          }}
        >
          <span
            className="rounded-full shrink-0"
            style={{ width: 6, height: 6, backgroundColor: text }}
          />
          {labelText}
        </span>
      ),
    };
  });

  const selectedCfg = newStatus ? STATUS_CONFIG[newStatus] : undefined;

  const reasonItems = UPDATE_SR_CANCELLATION_REASONS.map((r) => ({
    id: r.id,
    label: r.label,
    toolTip: false as const,
  }));
  const selectedReasonLabel =
    UPDATE_SR_CANCELLATION_REASONS.find((r) => r.id === cancellationReason)
      ?.label ?? '';

  // Missed-visit reason set (RR-916) — shares the technician reason codes.
  const missedVisitReasonItems = TECHNICIAN_MISSED_VISIT_REASONS.map((r) => ({
    id: r.id,
    label: r.label,
    toolTip: false as const,
  }));
  const selectedMissedVisitReasonLabel =
    TECHNICIAN_MISSED_VISIT_REASONS.find((r) => r.id === missedVisitReason)
      ?.label ?? '';
  const isOtherMissedVisitReason = missedVisitReason === 'other';

  // Primary CTA label/handler resolution. On the form step a detail-step
  // target ("cancelled" / "unfulfilled") reads "Continue"; the missed-visit
  // step submits via "Wyślij" (Send), the cancel step via "Confirm".
  const primaryLabel =
    step === 'form'
      ? needsDetailStep
        ? L.BUTTON_CONTINUE
        : isSubmitting
          ? L.BUTTON_SUBMITTING
          : L.BUTTON_SUBMIT
      : isSubmitting
        ? L.BUTTON_SUBMITTING
        : isMissedVisitStep
          ? TL.BUTTON_SEND
          : L.BUTTON_CONFIRM_CANCELLATION;

  const primaryDisabled =
    step === 'form'
      ? needsDetailStep
        ? !canContinue
        : !canSubmit
      : !canSubmit;

  const primaryOnClick = () => {
    if (step === 'form' && needsDetailStep) {
      onContinue();
      return;
    }
    onSubmit();
  };

  const secondaryLabel = step === 'form' ? L.BUTTON_CANCEL : L.BUTTON_BACK;
  const secondaryOnClick = step === 'form' ? onClose : onGoBackToForm;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={700}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      // overflow-visible! lets the cancellation-reason dropdown menu extend
      // beyond the modal's bottom edge. Scoped to this modal only.
      className="overflow-visible!"
      data-testid="update-sr-status-modal"
    >
      <div className="flex flex-col relative">
        <div className="px-6 pt-6 pb-2 flex flex-col gap-4">
          <h2
            className="text-neutral-10 font-normal"
            style={{ fontSize: 22, lineHeight: '28px' }}
            data-testid="update-sr-status-title"
          >
            {L.TITLE}
          </h2>
          {(showScheduledVisit || isMissedVisitStep) && (
            <p
              className="text-[#474A4C]"
              style={{
                fontFamily: 'Roboto',
                fontSize: 14,
                lineHeight: '20px',
                fontWeight: 400,
                letterSpacing: 0.25,
              }}
              data-testid="update-sr-status-cancel-subtitle"
            >
              {isMissedVisitStep
                ? TL.UNFULFILLED_SUBTITLE
                : L.CANCEL_STEP_SUBTITLE}
            </p>
          )}
        </div>

        <div className="px-6 pt-4 flex flex-col gap-8">
          {step === 'form' && (
            <>
              <div
                className="relative rounded-lg bg-[#E5E7E9] opacity-60 cursor-not-allowed px-3 w-[318px] max-w-full"
                style={{ minHeight: 56 }}
                data-testid="update-sr-status-current-field"
                aria-disabled="true"
              >
                <span
                  className="absolute left-3 pointer-events-none"
                  style={{
                    top: 0,
                    paddingTop: 8,
                    fontSize: 12,
                    lineHeight: '16px',
                    color: '#6a7278',
                  }}
                  data-testid="update-sr-status-current-label"
                >
                  {L.FIELD_CURRENT_STATUS}
                </span>
                <div
                  className="pt-7 pb-2"
                  data-testid="update-sr-current-status-value"
                >
                  <SRStatusBadge status={currentStatus} />
                </div>
                <div
                  className="absolute bottom-0"
                  style={{
                    left: 6,
                    right: 6,
                    height: 1,
                    backgroundColor: '#6a7278',
                  }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <div
                  className="w-[318px] max-w-full"
                  data-field="update-sr-new-status"
                >
                  <Dropdown
                    menuFullWidth
                    toolTip={false}
                    disabled={!hasTransitions || isSubmitting}
                    open={isDropdownOpen}
                    onOpenChange={setIsDropdownOpen}
                    items={
                      hasTransitions
                        ? items
                        : [
                            {
                              id: '__none__',
                              label: L.EMPTY_NO_OPTIONS,
                              disabled: true,
                            },
                          ]
                    }
                    {...(newStatus ? { selectedId: newStatus } : {})}
                    onItemClick={(id) => {
                      if (
                        allowedStatuses.includes(id as ServiceRequestStatus)
                      ) {
                        onNewStatusChange(id as ServiceRequestStatus);
                      }
                    }}
                    error={Boolean(displayNewStatusError)}
                    errorMessage={displayNewStatusError}
                    {...(!displayNewStatusError && !hasTransitions
                      ? { supportingText: L.HELPER_NO_TRANSITIONS }
                      : {})}
                    trigger={({ open: isOpen, toggle, disabled, error }) => {
                      const labelFloating = isOpen || !!selectedCfg;
                      const labelColor = isOpen ? '#2a4455' : '#6a7278';
                      return (
                        <button
                          type="button"
                          onClick={disabled ? undefined : toggle}
                          aria-expanded={isOpen}
                          aria-haspopup="true"
                          aria-disabled={disabled}
                          data-testid="update-sr-new-status-button"
                          className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${
                            disabled
                              ? 'cursor-not-allowed text-neutral-60'
                              : 'cursor-pointer text-neutral-10'
                          }`}
                        >
                          <div
                            className="flex-1 min-w-0 relative flex flex-col justify-center"
                            style={{ minHeight: 40 }}
                          >
                            <span
                              className="absolute left-0 pointer-events-none origin-top-left font-normal"
                              style={{
                                color: labelColor,
                                top: labelFloating ? 0 : 10,
                                fontSize: labelFloating ? 12 : 16,
                                lineHeight: labelFloating ? '16px' : '20px',
                                transition:
                                  'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                              }}
                            >
                              {L.FIELD_NEW_STATUS}
                            </span>
                            {selectedCfg && (
                              <span
                                className="inline-flex items-center gap-2 rounded-lg self-start"
                                style={{
                                  backgroundColor: selectedCfg.bg,
                                  color: selectedCfg.text,
                                  paddingLeft: 8,
                                  paddingRight: 12,
                                  paddingTop: 4,
                                  paddingBottom: 4,
                                  marginTop: 16,
                                  fontSize: 14,
                                  lineHeight: '20px',
                                  letterSpacing: '0.1px',
                                  fontWeight: 500,
                                }}
                              >
                                <span
                                  className="rounded-full shrink-0"
                                  style={{
                                    width: 6,
                                    height: 6,
                                    backgroundColor: selectedCfg.text,
                                  }}
                                />
                                {selectedCfg.label}
                              </span>
                            )}
                          </div>
                          <ChevronDown
                            strokeWidth={1.5}
                            className={`shrink-0 transition-transform duration-200 ${
                              isOpen ? 'rotate-180' : ''
                            } ${error ? 'text-error-40' : ''}`}
                          />
                        </button>
                      );
                    }}
                  />
                </div>

                <div
                  className="flex items-start gap-2"
                  data-testid="update-sr-status-irreversible-banner"
                >
                  <InfoIcon />
                  <p
                    style={{
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#474A4C',
                    }}
                  >
                    <span className="font-semibold">
                      {L.BANNER_IRREVERSIBLE_BOLD}
                    </span>
                    {L.BANNER_IRREVERSIBLE_REST}
                  </p>
                </div>
              </div>

              {!needsDetailStep && (
                <div data-field="update-sr-admin-notes">
                  <Textarea
                    label={
                      <>
                        {L.FIELD_ADMIN_NOTES}{' '}
                        <span style={{ fontSize: 12 }}>
                          {L.FIELD_ADMIN_NOTES_OPTIONAL}
                        </span>
                      </>
                    }
                    placeholder={L.PLACEHOLDER_ADMIN_NOTES}
                    value={adminNotes}
                    onChange={(e) =>
                      onAdminNotesChange(
                        (e.target as HTMLTextAreaElement).value ?? ''
                      )
                    }
                    maxLength={L.NOTES_MAX_LENGTH}
                    rows={4}
                    fullWidth
                    error={Boolean(adminNotesError)}
                    errorMessage={adminNotesError}
                    disabled={isSubmitting}
                    dataTestId="update-sr-admin-notes-input"
                  />
                </div>
              )}

              {!needsDetailStep && !assigneeMode && (
                <div className="flex items-center -ml-2">
                  <Checkbox
                    checked={notify}
                    onChange={(checked) => onNotifyChange(checked)}
                    disabled={isSubmitting}
                    label={SERVICE_MANAGEMENT_LABELS.FIELD_NOTIFY.polish}
                    labelClassName="!text-bodyMedium !font-normal text-neutral-10"
                    testId="update-sr-notify-checkbox"
                  />
                </div>
              )}
            </>
          )}

          {step === 'cancel-reason' && (
            <>
              {showScheduledVisit && <ScheduledVisitCard summary={summary} />}

              <div
                className="flex flex-col gap-2 w-[318px] max-w-full"
                data-field="update-sr-cancellation-reason"
              >
                <Dropdown
                  menuFullWidth
                  toolTip={false}
                  menuMaxVisibleItems={4}
                  disabled={isSubmitting}
                  open={isReasonDropdownOpen}
                  onOpenChange={setIsReasonDropdownOpen}
                  items={reasonItems}
                  {...(cancellationReason
                    ? { selectedId: cancellationReason }
                    : {})}
                  onItemClick={(id) =>
                    onCancellationReasonChange(
                      id as UpdateSrCancellationReasonId
                    )
                  }
                  error={Boolean(displayCancellationReasonError)}
                  errorMessage={displayCancellationReasonError}
                  trigger={({ open: isOpen, toggle, disabled, error }) => {
                    const labelFloating = isOpen || !!cancellationReason;
                    const labelColor = isOpen ? '#2a4455' : '#6a7278';
                    return (
                      <button
                        type="button"
                        onClick={disabled ? undefined : toggle}
                        aria-expanded={isOpen}
                        aria-haspopup="true"
                        aria-disabled={disabled}
                        data-testid="update-sr-cancellation-reason-button"
                        className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${
                          disabled
                            ? 'cursor-not-allowed text-neutral-60'
                            : 'cursor-pointer text-neutral-10'
                        }`}
                      >
                        <div
                          className="flex-1 min-w-0 relative flex flex-col justify-center"
                          style={{ minHeight: 40 }}
                        >
                          <span
                            className="absolute left-0 pointer-events-none origin-top-left font-normal"
                            style={{
                              color: labelColor,
                              top: labelFloating ? 0 : 10,
                              fontSize: labelFloating ? 12 : 16,
                              lineHeight: labelFloating ? '16px' : '20px',
                              transition:
                                'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                            }}
                          >
                            {L.CANCEL_STEP_REASON_LABEL}
                          </span>
                          {selectedReasonLabel && (
                            <span
                              className="self-start"
                              style={{
                                marginTop: 16,
                                fontSize: 14,
                                lineHeight: '20px',
                                fontWeight: 400,
                                color: '#181A1B',
                              }}
                            >
                              {selectedReasonLabel}
                            </span>
                          )}
                        </div>
                        <ChevronDown
                          strokeWidth={1.5}
                          className={`shrink-0 transition-transform duration-200 ${
                            isOpen ? 'rotate-180' : ''
                          } ${error ? 'text-error-40' : ''}`}
                        />
                      </button>
                    );
                  }}
                />
              </div>

              {cancellationReason === 'other' && (
                <div data-field="update-sr-custom-reason">
                  <Textarea
                    label={L.CANCEL_STEP_CUSTOM_REASON_LABEL}
                    placeholder={L.CANCEL_STEP_CUSTOM_REASON_PLACEHOLDER}
                    value={customReason}
                    onChange={(e) =>
                      onCustomReasonChange(
                        (e.target as HTMLTextAreaElement).value ?? ''
                      )
                    }
                    maxLength={L.NOTES_MAX_LENGTH}
                    rows={4}
                    fullWidth
                    error={Boolean(customReasonError)}
                    errorMessage={customReasonError}
                    disabled={isSubmitting}
                    dataTestId="update-sr-custom-reason-input"
                  />
                </div>
              )}
            </>
          )}

          {/* Missed-visit step  — read-only visit card,
              a mandatory reason dropdown, and a comment required only for the
              "Other" reason. The comment reuses the admin-notes field. */}
          {isMissedVisitStep && (
            <>
              <ScheduledVisitCard summary={summary} />

              <div
                className="flex flex-col gap-2 w-[318px] max-w-full"
                data-field="update-sr-missed-visit-reason"
              >
                <Dropdown
                  menuFullWidth
                  toolTip={false}
                  menuMaxVisibleItems={4}
                  disabled={isSubmitting}
                  open={isReasonDropdownOpen}
                  onOpenChange={setIsReasonDropdownOpen}
                  items={missedVisitReasonItems}
                  {...(missedVisitReason
                    ? { selectedId: missedVisitReason }
                    : {})}
                  onItemClick={(id) =>
                    onMissedVisitReasonChange?.(
                      id as TechnicianMissedVisitReasonId
                    )
                  }
                  error={Boolean(displayMissedVisitReasonError)}
                  errorMessage={displayMissedVisitReasonError}
                  trigger={({ open: isOpen, toggle, disabled, error }) => {
                    const labelFloating = isOpen || !!missedVisitReason;
                    const labelColor = isOpen ? '#2a4455' : '#6a7278';
                    return (
                      <button
                        type="button"
                        onClick={disabled ? undefined : toggle}
                        aria-expanded={isOpen}
                        aria-haspopup="true"
                        aria-disabled={disabled}
                        data-testid="update-sr-missed-visit-reason-button"
                        className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${
                          disabled
                            ? 'cursor-not-allowed text-neutral-60'
                            : 'cursor-pointer text-neutral-10'
                        }`}
                      >
                        <div
                          className="flex-1 min-w-0 relative flex flex-col justify-center"
                          style={{ minHeight: 40 }}
                        >
                          <span
                            className="absolute left-0 pointer-events-none origin-top-left font-normal"
                            style={{
                              color: labelColor,
                              top: labelFloating ? 0 : 10,
                              fontSize: labelFloating ? 12 : 16,
                              lineHeight: labelFloating ? '16px' : '20px',
                              transition:
                                'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                            }}
                          >
                            {
                              SERVICE_MANAGEMENT_LABELS
                                .FIELD_MISSED_VISIT_REASON.polish
                            }
                          </span>
                          {selectedMissedVisitReasonLabel && (
                            <span
                              className="self-start"
                              style={{
                                marginTop: 16,
                                fontSize: 14,
                                lineHeight: '20px',
                                fontWeight: 400,
                                color: '#181A1B',
                              }}
                            >
                              {selectedMissedVisitReasonLabel}
                            </span>
                          )}
                        </div>
                        <ChevronDown
                          strokeWidth={1.5}
                          className={`shrink-0 transition-transform duration-200 ${
                            isOpen ? 'rotate-180' : ''
                          } ${error ? 'text-error-40' : ''}`}
                        />
                      </button>
                    );
                  }}
                />
              </div>

              <div data-field="update-sr-missed-visit-comment">
                <Textarea
                  label={
                    <>
                      {TL.FIELD_MISSED_VISIT_COMMENT}
                      {!isOtherMissedVisitReason && (
                        <>
                          {' '}
                          <span style={{ fontSize: 12 }}>
                            {TL.FIELD_MISSED_VISIT_COMMENT_OPTIONAL}
                          </span>
                        </>
                      )}
                    </>
                  }
                  placeholder={TL.PLACEHOLDER_MISSED_VISIT_COMMENT}
                  value={adminNotes}
                  onChange={(e) =>
                    onAdminNotesChange(
                      (e.target as HTMLTextAreaElement).value ?? ''
                    )
                  }
                  maxLength={L.NOTES_MAX_LENGTH}
                  rows={4}
                  fullWidth
                  error={Boolean(adminNotesError)}
                  errorMessage={adminNotesError}
                  disabled={isSubmitting}
                  dataTestId="update-sr-missed-visit-comment-input"
                />
              </div>
            </>
          )}

          {submitError && (
            <p
              className="text-error-40"
              style={{ fontSize: 12, lineHeight: '16px' }}
              data-testid="update-sr-submit-error"
            >
              {submitError}
            </p>
          )}
        </div>

        <div
          className="flex items-center justify-end gap-3 px-6 py-6"
          data-dropdown-boundary
        >
          <button
            type="button"
            onClick={secondaryOnClick}
            disabled={isSubmitting}
            className={`h-10 px-5 rounded-lg border border-[#DCDDE0] bg-white text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#F3F4F6] cursor-pointer'
            }`}
            data-testid="update-sr-status-cancel"
          >
            {secondaryLabel}
          </button>
          <button
            type="button"
            onClick={primaryOnClick}
            disabled={primaryDisabled}
            className={`h-10 px-5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              primaryDisabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#1f3340] cursor-pointer'
            }`}
            data-testid="update-sr-status-submit"
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default UpdateStatusModal;
